import React, { useEffect, useMemo } from 'react';

import { Controller, UseFormReturn } from 'react-hook-form';

import { useVersionStore } from 'store/version.store';

import { useQueryGetCardResourceInit } from 'api/queries/getCardResourceInit.query';

import classes from 'components/common/GlobalFiltersInPage/Filters.module.scss';
import { FiltersStatus } from 'components/common/GlobalFiltersInPage/GlobalFiltersInPage';
import { FilterLabels } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';
import { signalSingleFilterIsReady } from 'components/common/GlobalFiltersInPage/hooks/useSubscribersFiltersReady';

import { Select } from 'components/DesignSystem/Select';

type Option = {
    id: number;
    value: number;
    label: string;
};

interface Props {
    methods?: UseFormReturn<any, any>;
    name: string;
    filtersStatusRef?: React.MutableRefObject<FiltersStatus>;
    handleFilters?: () => void;
    value?: string[];
    isSelected?: boolean;
    versionId?: number;
}

export const ResourceNameFilter = ({
    name,
    methods,
    filtersStatusRef,
    handleFilters,
    value,
    isSelected = true,
    versionId,
}: Props) => {
    const { data, isSuccess } = useQueryGetCardResourceInit({
        metaDataId: versionId,
    });

    const options: Option[] = useMemo(() => {
        return (
            data?.map((option) => {
                return {
                    id: option?.value,
                    value: option.value,
                    label: `${option.label} (~ ${option.count} related activities)`,
                };
            }) ?? []
        );
    }, [data]);

    useEffect(() => {
        if (!filtersStatusRef?.current || filtersStatusRef?.current[name]?.isReady || !isSuccess) {
            return;
        }

        signalSingleFilterIsReady({ filterId: name, isReady: isSuccess });
    }, [value, name, filtersStatusRef, isSuccess]);

    const handleValue = (formValue?: number[]): Option[] => {
        const result: Option[] = [];
        if (!formValue?.length) return result;

        options.forEach((listOptionElement: Option) => {
            const currentFormValueElement = formValue.find(
                (formValueElement) => parseInt(formValueElement) === listOptionElement.value,
            );

            if (currentFormValueElement) {
                result.push(listOptionElement);
            }
        });

        return result;
    };

    const handleChange = (selected: readonly ISelectOption<string>[]) => {
        methods?.setValue(
            name,
            selected.map((option) => option.value),
        );
        handleFilters && handleFilters();
    };

    return isSelected ? (
        <div data-testid={'battlecards-filters-resource-name'} className={classes.selectWrapper}>
            <Controller
                render={({ field }) => (
                    <Select<ISelectOption<number> & { count: number }, true>
                        id={'battlecards-resource-name'}
                        {...field}
                        options={options}
                        isMulti
                        value={handleValue(value)}
                        onChange={handleChange}
                        hideSelectedOptions={true}
                        label={FilterLabels.RESOURCE_NAME_CARD_BOARD}
                    />
                )}
                name={name}
                control={methods?.control}
            />
        </div>
    ) : null;
};
